import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  BadgeCheck,
  ChevronRight,
  Copy,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Search,
  Send,
} from "lucide-react";

import { FilterChip, ListState, StatusPill } from "@/components/admin/primitives";
import { PreviewStage } from "@/components/PreviewStage";
import { formatDate } from "@/lib/dates";
import { countLabel, formatBRL, padOrderNumber } from "@/lib/format";
import { ORDER_STATUSES, ORDER_STATUS_LABEL, isEditableStatus, type OrderStatus } from "@/lib/orders";
import { useDuplicateMyOrder, useMyOrders, useSendMyOrder } from "@/lib/queries";

/**
 * Meus pedidos (cliente).
 *
 * O que o cliente ainda precisa fazer vem na frente: pedido salvo tem
 * "continuar montando" e "enviar", e pedido pendente de aprovacao tem o
 * aviso no topo e o atalho para revisar.
 */
export const Route = createFileRoute("/minha-conta/_conta/pedidos/")({
  head: () => ({ meta: [{ title: "Meus pedidos — MonteBox" }] }),
  component: ClientePedidos,
});

type StatusFilter = OrderStatus | "todos";

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "todos", label: "Todos" },
  ...ORDER_STATUSES.map((value) => ({ value, label: ORDER_STATUS_LABEL[value] })),
];

function ClientePedidos() {
  const [status, setStatus] = useState<StatusFilter>("todos");
  const [query, setQuery] = useState("");

  const myOrders = useMyOrders();
  const duplicate = useDuplicateMyOrder();
  const send = useSendMyOrder();

  const awaitingApproval = (myOrders.data ?? []).filter(
    (order) => order.status === "pendente_aprovacao",
  );

  // A lista da conta e curta: filtrar no cliente evita ida e volta a cada tecla.
  const orders = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (myOrders.data ?? []).filter((order) => {
      if (status !== "todos" && order.status !== status) return false;
      if (!term) return true;
      return [padOrderNumber(order.number), ...order.items.map((item) => item.name)]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [myOrders.data, status, query]);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 sm:gap-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-mb-navy">
            Meus pedidos
          </h1>
          <p className="mt-2 max-w-xl text-sm text-mb-muted text-pretty">
            Tudo que você montou fica salvo aqui. Envie para a equipe, acompanhe cada etapa,
            aprove a arte e repita um pedido antigo quando quiser.
          </p>
        </div>
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2 rounded-mb-button bg-mb-yellow px-5 py-3.5 font-display text-sm font-bold text-mb-ink shadow-cta transition-transform hover:-translate-y-0.5"
        >
          <Plus className="h-[18px] w-[18px] stroke-[2.5]" />
          Montar novo pedido
        </Link>
      </div>

      {awaitingApproval.length > 0 && (
        <div className="flex items-center gap-4 rounded-mb-card border border-orange-300 bg-orange-50 px-5 py-4">
          <BadgeCheck className="h-5 w-5 shrink-0 text-orange-700" />
          <p className="grow text-sm text-orange-900">
            <span className="font-bold">
              {awaitingApproval.length === 1
                ? "1 pedido aguarda a sua aprovação."
                : `${awaitingApproval.length} pedidos aguardam a sua aprovação.`}
            </span>{" "}
            A equipe mandou a arte no seu WhatsApp — responda por lá para seguir para a produção.
          </p>
          <Link
            to="/minha-conta/pedidos/$pedidoId"
            params={{ pedidoId: awaitingApproval[0]!.id }}
            className="shrink-0 rounded-full bg-orange-700 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-orange-800"
          >
            Ver #{padOrderNumber(awaitingApproval[0]!.number)}
          </Link>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((filter) => (
          <FilterChip
            key={filter.value}
            active={status === filter.value}
            onClick={() => setStatus(filter.value)}
          >
            {filter.label}
          </FilterChip>
        ))}
        <label className="flex h-9 w-full items-center gap-2.5 rounded-full border border-mb-line bg-white px-4 focus-within:border-mb-navy sm:ml-auto sm:w-[280px]">
          <Search className="h-4 w-4 shrink-0 text-mb-muted" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar por número ou peça"
            className="w-full bg-transparent text-sm text-mb-ink outline-none placeholder:text-mb-muted"
          />
        </label>
      </div>

      <div className="flex flex-col gap-4">
        <div className="rounded-mb-card border border-mb-line bg-white empty:hidden">
          <ListState
            loading={myOrders.isLoading}
            error={myOrders.error}
            empty={orders.length === 0}
            emptyLabel={
              (myOrders.data?.length ?? 0) === 0
                ? "Você ainda não tem pedidos guardados."
                : "Nenhum pedido com esse filtro."
            }
          />
        </div>

        {orders.map((order) => {
          const draft = order.status === "rascunho";
          const editable = isEditableStatus(order.status);
          const pieces = order.items.reduce((sum, item) => sum + item.quantity, 0);

          return (
            <div
              key={order.id}
              className="flex flex-wrap items-center gap-4 rounded-mb-card border border-mb-line bg-white p-4 shadow-card sm:flex-nowrap sm:gap-5 sm:p-5"
            >
              {/*
                Só a primeira peça no cartão: a 104px, o kit inteiro empilhado
                vira uma mancha. A composição completa aparece no pedido aberto.
              */}
              <PreviewStage
                items={order.items.slice(0, 1)}
                className="h-20 w-20 shrink-0 rounded-mb-button sm:h-26 sm:w-26"
              />

              <div className="min-w-0 flex-1 basis-40">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-display text-lg font-bold tabular-nums text-mb-navy">
                    #{padOrderNumber(order.number)}
                  </span>
                  <StatusPill status={order.status} />
                  <span className="text-xs text-mb-muted">{formatDate(order.createdAt)}</span>
                </div>

                <p className="mt-1.5 text-sm font-bold text-mb-ink">
                  {order.items.map((item) => item.name).join(" · ")}
                </p>
                <p className="mt-1 text-xs text-mb-muted">
                  {countLabel(pieces, "peça", "peças")} em{" "}
                  {countLabel(order.items.length, "item configurado", "itens configurados")}
                </p>
              </div>

              <div className="shrink-0 text-right">
                <p className="text-[11px] font-bold uppercase leading-4 tracking-[0.1em] text-mb-muted">
                  {draft ? "Parcial" : "Total"}
                </p>
                <p className="font-display text-xl font-bold tabular-nums text-mb-navy">
                  {formatBRL(order.total)}
                </p>
              </div>

              <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto sm:shrink-0">
                {editable ? (
                  <>
                    <Link
                      to="/"
                      search={{ pedido: order.id }}
                      className="flex items-center gap-1.5 rounded-full border border-mb-line px-3.5 py-2 text-xs font-bold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Continuar montando
                    </Link>
                    <button
                      type="button"
                      disabled={send.isPending}
                      onClick={() =>
                        send.mutate(order.id, {
                          onSuccess: () =>
                            toast.success(
                              `Pedido #${padOrderNumber(order.number)} enviado para a equipe!`,
                            ),
                          onError: (cause) =>
                            toast.error(
                              cause instanceof Error
                                ? cause.message
                                : "Não foi possível enviar o pedido.",
                            ),
                        })
                      }
                      className="flex items-center gap-1.5 rounded-full bg-mb-yellow px-4 py-2 text-xs font-bold text-mb-ink shadow-cta transition-transform hover:-translate-y-0.5 disabled:opacity-50"
                    >
                      {send.isPending && send.variables === order.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="h-3.5 w-3.5" />
                      )}
                      Enviar
                    </button>
                    <Link
                      to="/minha-conta/pedidos/$pedidoId"
                      params={{ pedidoId: order.id }}
                      aria-label={`Ver o pedido ${padOrderNumber(order.number)}`}
                      className="grid h-8 w-8 place-items-center rounded-full border border-mb-line text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </>
                ) : order.status === "pendente_aprovacao" ? (
                  <Link
                    to="/minha-conta/pedidos/$pedidoId"
                    params={{ pedidoId: order.id }}
                    className="flex items-center gap-1.5 rounded-full bg-orange-700 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-orange-800"
                  >
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Ver a arte
                  </Link>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={duplicate.isPending}
                      onClick={() =>
                        duplicate.mutate(order.id, {
                          onSuccess: (copy) =>
                            toast.success(
                              `Pedido repetido como #${padOrderNumber(copy.number)} (rascunho).`,
                            ),
                          onError: () => toast.error("Não foi possível repetir o pedido."),
                        })
                      }
                      className="flex items-center gap-1.5 rounded-full border border-mb-line px-3.5 py-2 text-xs font-bold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface disabled:opacity-50"
                    >
                      {duplicate.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                      Repetir
                    </button>
                    <Link
                      to="/minha-conta/pedidos/$pedidoId"
                      params={{ pedidoId: order.id }}
                      className="flex items-center gap-1.5 rounded-full bg-mb-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-mb-ink"
                    >
                      Ver pedido
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="flex items-center justify-center gap-2 text-xs text-mb-muted">
        <Lock className="h-3.5 w-3.5" />
        Pedidos ficam guardados na sua conta. Só você e a equipe MonteBox enxergam.
      </p>
    </div>
  );
}
